import { useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { generatePDFOptions, PDF_CONFIG } from '../utils/pdfConfig';

export const usePDFGenerator = () => {
  const generatePDF = useCallback(async (elementRef) => {
    try {
      const element = elementRef.current;
      if (!element) {
        console.error('Elemento no encontrado');
        return;
      }

      // Verificar que el elemento tiene dimensiones válidas
      if (element.offsetWidth === 0 || element.offsetHeight === 0) {
        console.error('El elemento no tiene dimensiones válidas');
        alert('Error: El contenido no está completamente cargado. Por favor, espera un momento e intenta nuevamente.');
        return;
      }

      // Tiempo de espera optimizado para renderizado completo
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Validación optimizada de elementos canvas
      const canvasElements = element.querySelectorAll('canvas');
      const invalidCanvas = Array.from(canvasElements).find(canvas => 
        canvas.width === 0 || canvas.height === 0
      );
      
      if (invalidCanvas) {
        console.error('Canvas encontrado con dimensiones inválidas:', invalidCanvas);
        alert('Error: El gráfico no se ha cargado completamente. Por favor, espera un momento e intenta nuevamente.');
        return;
      }

      // Generar canvas del elemento
      const canvas = await html2canvas(element, generatePDFOptions(element));

      // Crear PDF
      const pdf = new jsPDF(PDF_CONFIG.orientation, PDF_CONFIG.unit, PDF_CONFIG.format);
      
      // Obtener dimensiones del PDF en puntos
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Configurar márgenes y área disponible
      const margin = 20;
      const availableWidth = pdfWidth - (margin * 2);
      const availableHeight = pdfHeight - (margin * 2);
      
      // Convertir la imagen del canvas a base64
      const imgData = canvas.toDataURL('image/png');
      
      // Calcular dimensiones que ocupen todo el ancho disponible
      const aspectRatio = canvas.height / canvas.width;
      const imgWidth = availableWidth;
      const imgHeight = availableWidth * aspectRatio;
      
      // Centrar horizontalmente
      const imgX = margin;
      const imgY = margin;
      
      // Si la imagen cabe en una página, añadirla directamente
      if (imgHeight <= availableHeight) {
        pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth, imgHeight);
      } else {
        // Dividir en múltiples páginas
        const pageHeight = availableHeight;
        const totalPages = Math.ceil(imgHeight / pageHeight);
        
        for (let page = 0; page < totalPages; page++) {
          if (page > 0) {
            pdf.addPage();
          }
          
          const yOffsetInImg = page * pageHeight;
          const currentPageHeight = Math.min(pageHeight, imgHeight - yOffsetInImg);
          
          // Calcular qué porción del canvas original usar
          const sourceY = (canvas.height * yOffsetInImg) / imgHeight;
          const sourceHeight = (canvas.height * currentPageHeight) / imgHeight;
          
          // Crear un nuevo canvas para esta porción
          const pageCanvas = document.createElement('canvas');
          const ctx = pageCanvas.getContext('2d');
          pageCanvas.width = canvas.width;
          pageCanvas.height = sourceHeight;
          
          // Dibujar la porción correspondiente
          ctx.drawImage(
            canvas,
            0, sourceY, canvas.width, sourceHeight,  // fuente
            0, 0, canvas.width, sourceHeight         // destino
          );
          
          // Convertir a imagen y añadir al PDF
          const pageImgData = pageCanvas.toDataURL('image/png');
          pdf.addImage(pageImgData, 'PNG', imgX, imgY, imgWidth, currentPageHeight);
        }
      }
      
      // Abrir PDF en nueva pestaña
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      
      // Limpieza de memoria
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
      
    } catch (error) {
      console.error('Error al generar PDF:', error);
      alert('Error al generar el PDF. Por favor, intenta nuevamente.');
    }
  }, []);

  return { generatePDF };
};